import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/';
const API = `${BACKEND_URL}/api`;

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API,
});

// Add request interceptor to automatically add token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('mauma_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('mauma_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  getMe: () => axiosInstance.get('/auth/me'),

  // Locations
  getLocations: () => axiosInstance.get('/locations'),
  createLocation: (data) => axiosInstance.post('/locations', data),
  updateLocation: (id, data) => axiosInstance.put(`/locations/${id}`, data),
  deleteLocation: (id) => axiosInstance.delete(`/locations/${id}`),

  // Rooms
  getRooms: (locationId = null) => {
    const url = locationId ? `/rooms?location_id=${locationId}` : '/rooms';
    return axiosInstance.get(url);
  },
  createRoom: (data) => axiosInstance.post('/rooms', data),
  updateRoom: (id, data) => axiosInstance.put(`/rooms/${id}`, data),
  deleteRoom: (id) => axiosInstance.delete(`/rooms/${id}`),

  // Services
  getServices: (locationId = null) => {
    const url = locationId ? `/services?location_id=${locationId}` : '/services';
    return axiosInstance.get(url);
  },
  createService: (data) => axiosInstance.post('/services', data),
  updateService: (id, data) => axiosInstance.put(`/services/${id}`, data),
  deleteService: (id) => axiosInstance.delete(`/services/${id}`),

  // Prices
  getPrices: (serviceId = null) => {
    const url = serviceId ? `/prices?service_id=${serviceId}` : '/prices';
    return axiosInstance.get(url);
  },
  createPrice: (data) => axiosInstance.post('/prices', data),

  // Schedules
  getSchedules: (locationId = null) => {
    const url = locationId ? `/schedules?location_id=${locationId}` : '/schedules';
    return axiosInstance.get(url);
  },
  createSchedule: (data) => axiosInstance.post('/schedules', data),
  deleteSchedule: (id) => axiosInstance.delete(`/schedules/${id}`),

  // Staff
  getStaff: () => axiosInstance.get('/staff'),
  createStaff: (data) => axiosInstance.post('/staff', data),
  updateStaff: (id, data) => axiosInstance.put(`/staff/${id}`, data),
  deleteStaff: (id) => axiosInstance.delete(`/staff/${id}`),

  // Clients
  getClients: () => axiosInstance.get('/clients'),
  getClient: (id) => axiosInstance.get(`/clients/${id}`),
  createClient: (data) => axiosInstance.post('/clients', data),
  updateClient: (id, data) => axiosInstance.put(`/clients/${id}`, data),
  deleteClient: (id) => axiosInstance.delete(`/clients/${id}`),

  // Bookings
  getBookings: (startDate = null, endDate = null, locationId = null) => {
    let url = '/bookings';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (locationId) params.push(`location_id=${locationId}`);
    if (params.length) url += `?${params.join('&')}`;
    return axiosInstance.get(url);
  },
  getBooking: (id) => axiosInstance.get(`/bookings/${id}`),
  createBooking: (data) => axiosInstance.post('/bookings', data),
  updateBooking: (id, data) => axiosInstance.put(`/bookings/${id}`, data),
  updateBookingStatus: (id, status) => 
    axiosInstance.patch(`/bookings/${id}/status?status=${status}`),
  updateBookingPayment: (id, paymentStatus, additionalAmount = 0) => 
    axiosInstance.patch(
      `/bookings/${id}/payment?payment_status=${paymentStatus}&additional_amount=${additionalAmount}`
    ),
  deleteBooking: (id) => axiosInstance.delete(`/bookings/${id}`),

  // Transactions
  getTransactions: (startDate = null, endDate = null, type = null) => {
    let url = '/transactions';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (type) params.push(`type=${type}`);
    if (params.length) url += `?${params.join('&')}`;
    return axiosInstance.get(url);
  },
  createTransaction: (data) => axiosInstance.post('/transactions', data),
  getBalance: () => axiosInstance.get('/transactions/balance'),

  // Commissions
  getCommissions: (staffId = null, paid = null) => {
    let url = '/commissions';
    const params = [];
    if (staffId) params.push(`staff_id=${staffId}`);
    if (paid !== null) params.push(`paid=${paid}`);
    if (params.length) url += `?${params.join('&')}`;
    return axiosInstance.get(url);
  },
  payCommission: (id, paymentAmount = null) => {
    const url = `/commissions/${id}/pay${paymentAmount ? `?payment_amount=${paymentAmount}` : ''}`;
    return axiosInstance.patch(url);
  },
  getCommissionsSummary: () => axiosInstance.get('/commissions/summary'),

  // Dashboard
  getDashboardStats: () => axiosInstance.get('/dashboard/stats'),
};